/*!
 * © 2024. The EGT Universe. All Rights Reserved.
 * License: Requires ACTIVE AppArtisan Subscription.
 * For more information visit: https://egtuniverse.com/legal
 *
 * Created for Universe App Tools.
 *
 * Created by Justin K Kazmierczak.
 */
var _ = require("../../../uam/module.js")(exports, {
  namespace: "ua.wizard",
  title: "Wizard",
  control: true,
  preview: "ua.preview.json",
  description: "A step-by-step wizard for controlling a user guided process.",
  fields: {
    "inner": {
      title: "Inner Content",
      type: "array",
      "of": "inner",
      description: "The steps of the wizard, each with a title, description, and optional icon.",
      required: true
    },
    "name": {
      title: "Name",
      type: "string",
      description: "The submit URL of the wizard.",
      required: true
    },
    "progressbarclass": {
      title: "Progress Bar Class",
      type: "string",
      description: "The class of the progress bar.",
      default: "bg-primary"
    },
    "showProgressBar": {
      title: "Show Progress Bar",
      type: "boolean",
      description: "If true, shows the progress bar in the footer of the wizard. It's redunat based when you show step navigation, but can be useful for a more visual indicator of progress even without step titles.",
      default: false
    },
    "continueText": {
      title: "Continue Button Text",
      type: "string",
      description: "The text to display on the continue button.",
      default: ""
    },
    "submitText": {
      title: "Submit Button Text",
      type: "string",
      description: "The text to display on the final submit button.",
      default: ""
    },
    "showStepNavigation": {
      title: "Show Step Navigation",
      type: "boolean",
      description: "If true, shows step navigation at the top of the wizard.",
      default: false
    },
    "allowStepJump": {
      title: "Allow Step Jump",
      type: "boolean",
      description: "If true, step navigation can jump to any step without requiring previous visitation.",
      default: false
    },
    "allowStepJumpOnEdit": {
      title: "Allow Step Jump On Edit",
      type: "boolean",
      description: "If true, wizard auto-enables step jumping when editing an existing item.",
      default: true
    },
    "editing": {
      title: "Editing",
      type: "boolean",
      description: "Optional explicit edit-mode hint for auto step-jump behavior.",
      default: false
    },
    "dynamicStepsUrl": {
      title: "Dynamic Steps URL",
      type: "string",
      description: "Optional API route that can return additional wizard steps in UA/JSON as the user progresses.",
      default: ""
    },
    "dynamicStepsMethod": {
      title: "Dynamic Steps Method",
      type: "string",
      description: "HTTP method used for dynamic step requests.",
      default: "POST"
    },
    "dynamicStepsDataPath": {
      title: "Dynamic Steps Data Path",
      type: "string",
      description: "Response path for dynamic steps. Supports: steps, data.steps, dynamicSteps, data.dynamicSteps.",
      default: "auto"
    },
    "showSummaryStep": {
      title: "Show Summary Step",
      type: "boolean",
      description: "If true, adds a final summary step before submit.",
      default: true
    },
    "summary": {
      title: "Summary",
      type: "boolean",
      description: "Alias for showSummaryStep. If true, adds a final summary step before submit.",
      default: true
    },
    "showSummary": {
      title: "Show Summary",
      type: "boolean",
      description: "Alias for showSummaryStep. If true, adds a final summary step before submit.",
      default: true
    },
    "disableDefaultBehavior": {
      title: "Disable Default Behavior",
      type: "boolean",
      description: "If true, disables built-in wizard navigation/footer wiring for parent-managed embedded usage.",
      default: false
    },
    "successTitle": {
      title: "Success Title",
      type: "string",
      description: "Optional title shown on the final success confirmation screen.",
      default: ""
    },
    "successDescription": {
      title: "Success Description",
      type: "string",
      description: "Optional description shown on the final success confirmation screen.",
      default: ""
    },
    "successIcon": {
      title: "Success Icon",
      type: "string",
      description: "Bootstrap icon class suffix for the success confirmation screen.",
      default: "check2-circle"
    },
    "successDocumentFields": {
      title: "Success Document Fields",
      type: "string",
      description: "Comma-separated document field names to show in success details. Empty shows all non-sensitive fields.",
      default: ""
    },
    "successResponseFields": {
      title: "Success Response Fields",
      type: "string",
      description: "Comma-separated response field names to show in success details. Empty shows all non-sensitive fields.",
      default: ""
    },
    "successHideToolbar": {
      title: "Success Hide Toolbar",
      type: "boolean",
      description: "If true, hides the wizard footer after successful submission.",
      default: true
    },
    "successShowDocument": {
      title: "Success Show Document",
      type: "boolean",
      description: "If true, shows submitted document values on the success confirmation screen.",
      default: true
    },
    "successShowResponse": {
      title: "Success Show Response",
      type: "boolean",
      description: "If true, shows response values from the route on the success confirmation screen.",
      default: true
    },
    "successShowSent": {
      title: "Success Show Sent",
      type: "boolean",
      description: "If true, shows the What you sent section on the success confirmation screen.",
      default: true
    },
    "successShowAppResponse": {
      title: "Success Show App Response",
      type: "boolean",
      description: "If true, shows the App response section on the success confirmation screen.",
      default: true
    },
    "phi": {
      title: "Protected Health Information",
      type: "boolean",
      description: "If true, drafts are not stored in localStorage. Requires a draft-url for server-side draft saving.",
      default: false
    },
    "draft-url": {
      title: "Draft URL",
      type: "string",
      description: "Server endpoint for saving and loading PHI drafts.",
      default: ""
    },
    "errors": {
      title: "Error Overrides",
      type: "array",
      "of": "inner",
      description: "Wizard-specific error message overrides keyed by error namespace.",
      default: []
    }
  }
});

var handlerz = [];
var uai = require("../interface/interface.js");
var jsonRender = require("../interface/jsonRender.js");
var events = require("../../../uam/events.js");

// ─── Global error registry ────────────────────────────────────────────────────
// Priority order (higher wins):
// 0 = original error payload
// 1 = dashboard override
// 2 = plugin override
// 3 = app override
var _errorRegistry = {};
var ERROR_PRIORITY = {
  original: 0,
  dashboard: 1,
  plugin: 2,
  app: 3
};

function _registerError(namespace, def, priority) {
  var ns = (namespace || "").toString().trim();
  if (!ns || !def) return;
  var p = typeof priority === "number" ? priority : ERROR_PRIORITY.dashboard;
  // Higher number wins (more specific overrides more general)
  if (!_errorRegistry[ns] || p >= _errorRegistry[ns]._p) {
    _errorRegistry[ns] = {
      _p: p,
      namespace: ns,
      title: (def.title || "").toString(),
      description: (def.description || "").toString(),
      actions: def.actions || null,
      close: def.close !== false
    };
  }
}

function _sanitizePathString(text) {
  var origin = "";
  try {
    origin = window.location.origin || "";
  } catch (e) { /* ignore */ }
  var value = (text || "").toString();
  value = value.replace(/(\/(?:Users|home|var|opt|srv|tmp|private|Volumes|workspace|workspaces|app|usr\/src|root)\/[^\s)'"`]+)+/g, "@!/");
  if (origin) {
    value = value.split(origin).join("@!");
  }
  return value;
}

function _sanitizeStack(stack) {
  if (!stack) return stack;
  if (typeof stack === "string") return _sanitizePathString(stack);
  if (stack instanceof Array) {
    return stack.map(function(item) {
      if (typeof item === "string") return _sanitizePathString(item);
      if (item && typeof item === "object") {
        var next = { ...item };
        if ("file" in next) next.file = _sanitizePathString(next.file);
        if ("s" in next) next.s = _sanitizePathString(next.s);
        return next;
      }
      return item;
    });
  }
  return stack;
}

function _normalizeErrorObject(serverError) {
  if (!serverError) {
    return {
      namespace: "",
      title: "Validation Error",
      description: "Please review the highlighted fields and try again.",
      _priority: ERROR_PRIORITY.original
    };
  }

  if (typeof serverError === "string") {
    return {
      namespace: "",
      title: "Validation Error",
      description: serverError,
      _priority: ERROR_PRIORITY.original
    };
  }

  var err = (serverError.error && typeof serverError.error === "object") ? serverError.error : serverError;
  var title = err.title || err.name || "Validation Error";
  var description = err.description || err.message || "Please review the highlighted fields and try again.";

  return {
    ...err,
    namespace: (err.namespace || "").toString().trim(),
    title: title,
    description: description,
    stack: _sanitizeStack(err.stack),
    stacktrace: _sanitizeStack(err.stacktrace || err.stack),
    _priority: ERROR_PRIORITY.original
  };
}

function _resolveError(wizard, serverError) {
  var baseError = _normalizeErrorObject(serverError);
  var ns = baseError.namespace;

  var dashboardOverride = null;
  if (ns) {
    try {
      var wizardErrors = JSON.parse(wizard.getAttribute("data-wizard-errors") || "{}");
      if (wizardErrors[ns]) {
        dashboardOverride = {
          ...wizardErrors[ns],
          namespace: ns,
          _priority: ERROR_PRIORITY.dashboard
        };
      }
    } catch (e) { /* ignore */ }
  }

  var registryOverride = null;
  if (ns && _errorRegistry[ns]) {
    registryOverride = _errorRegistry[ns];
  }

  var best = baseError;
  if (dashboardOverride && dashboardOverride._priority >= best._priority) {
    best = { ...best, ...dashboardOverride };
  }
  if (registryOverride && registryOverride._p >= best._priority) {
    best = { ...best, ...registryOverride, _priority: registryOverride._p };
  }

  return best;
}

// ─── Draft helpers ────────────────────────────────────────────────────────────

function _draftKey(wizard) {
  var uaeEl = wizard.closest("uae");
  return "ua-wiz:" + ((uaeEl && uaeEl.getAttribute("name")) || wizard.getAttribute("id") || "");
}

function _isPhiWizard(wizard) {
  return wizard.getAttribute("data-wizard-phi") === "true";
}

function _isDashboardContext(wizard) {
  return !!wizard.closest("[data-ua-init-type]");
}

function _loadDraft(wizard) {
  try {
    var raw = localStorage.getItem(_draftKey(wizard));
    if (!raw) return null;
    var json = decodeURIComponent(escape(atob(raw)));
    return JSON.parse(json);
  } catch (e) {
    return null;
  }
}

function _saveDraft(wizard, data) {
  if (_isPhiWizard(wizard)) {
    _saveServerDraft(wizard, data);
    return;
  }
  try {
    var json = JSON.stringify(data);
    var encoded = btoa(unescape(encodeURIComponent(json)));
    localStorage.setItem(_draftKey(wizard), encoded);
  } catch (e) { /* ignore */ }
}

function _clearDraft(wizard) {
  if (_isPhiWizard(wizard)) {
    var draftUrl = (wizard.getAttribute("data-wizard-draft-url") || "").trim();
    if (draftUrl) {
      fetch(draftUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "clear" })
      }).catch(function() {});
    }
    return;
  }
  try {
    localStorage.removeItem(_draftKey(wizard));
  } catch (e) { /* ignore */ }
}

// A phi wizard never reads or writes its localStorage draft key — but a browser
// that drafted under a PRE-phi build still holds that stale key (base64, and it
// can contain data the phi flag exists to keep out of localStorage). phi's paths
// never overwrite it, so it lingers in same-origin storage forever. Proactively
// remove it once on mount. Guarded so private mode / disabled storage can't throw.
function _purgePhiLocalDraft(wizard) {
  if (!_isPhiWizard(wizard)) return;
  try {
    localStorage.removeItem(_draftKey(wizard));
  } catch (e) { /* localStorage unavailable (private mode) — nothing to purge */ }
}

function _saveServerDraft(wizard, data) {
  var draftUrl = (wizard.getAttribute("data-wizard-draft-url") || "").trim();
  if (!draftUrl) return;
  fetch(draftUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action: "save", data: data })
  }).catch(function() {});
}

async function _loadServerDraft(wizard) {
  var draftUrl = (wizard.getAttribute("data-wizard-draft-url") || "").trim();
  if (!draftUrl) return null;
  try {
    var response = await fetch(draftUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "get" })
    });
    if (!response.ok) return null;
    var result = await response.json();
    if (result && result.success && result.data) return result.data;
  } catch (e) { /* ignore */ }
  return null;
}

async function _captureDraft(wizard) {
  var doc = await _collectDocument(wizard, true);
  _saveDraft(wizard, doc.flat);
}

function _attachDraftListeners(wizard) {
  wizard.addEventListener("change", function() {
    _captureDraft(wizard); // intentionally not awaited — fire-and-forget
  });
  wizard.addEventListener("focusout", function(e) {
    var t = e.target;
    if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT")) {
      _captureDraft(wizard); // intentionally not awaited — fire-and-forget
    }
  });
}

// ─── Document collection (all steps, via UATools.save per-step) ───────────────

/**
 * Collects the full document being built by the wizard across all steps.
 * Uses window.UATools.save() per step (no validation) so complex controls
 * serialise correctly. Falls back to _collectFields() when UATools is not
 * available (e.g. during server-side render).
 * @param {HTMLElement} wizard The [data-wizard='true'] element.
 * @returns {Promise<{flat: object, steps: Array}>}
 */
function _getTopLevelStepControls(stepEl) {
  var controls = [];
  if (!stepEl || !stepEl.querySelectorAll) return controls;

  var candidates = stepEl.querySelectorAll("uae[name]");
  for (var i = 0; i < candidates.length; i++) {
    var control = candidates[i];
    var name = control.getAttribute("name");
    if (!name) continue;

    var ancestor = control.parentElement ? control.parentElement.closest("uae[name]") : null;
    if (ancestor && stepEl.contains(ancestor)) continue;

    controls.push(control);
  }

  return controls;
}

function _getTopLevelStepControlNames(stepEl) {
  var names = {};
  var controls = _getTopLevelStepControls(stepEl);
  for (var i = 0; i < controls.length; i++) {
    var name = controls[i].getAttribute("name");
    if (!name) continue;
    names[name] = true;
  }
  return names;
}

// Background collectors (draft autosave, dynamic-step lookahead, pre-submit summary)
// need each control's data but must NOT paint validation UI — only an explicit
// _validateStep (Continue/Next click) should do that. Individual controls' own
// save() unconditionally flips is-invalid/is-valid as a side effect of computing
// data, so when collecting silently we snapshot the classList of the control and
// its inputs beforehand and restore it afterward, undoing that side effect.
function _snapshotValidityClasses(control) {
  var nodes = [control].concat(Array.prototype.slice.call(control.querySelectorAll("input, select, textarea")));
  return nodes.map(function(node) {
    return {
      node: node,
      invalid: node.classList.contains("is-invalid"),
      valid: node.classList.contains("is-valid")
    };
  });
}

function _restoreValidityClasses(snapshot) {
  for (var i = 0; i < snapshot.length; i++) {
    var entry = snapshot[i];
    entry.node.classList.toggle("is-invalid", entry.invalid);
    entry.node.classList.toggle("is-valid", entry.valid);
  }
}

async function _saveTopLevelControl(control, silent) {
  if (!(control && control.getAttribute)) return { success: false, data: undefined };

  var namespace = (control.getAttribute("namespace") || "").toString().trim();
  var name = (control.getAttribute("name") || "").toString().trim();
  if (!name) return { success: false, data: undefined };

  var element = namespace ? jsonRender.search(namespace) : null;
  if (element && typeof element.save === "function") {
    var repo = {
      success: true,
      data: {},
      namespace: namespace,
      errors: []
    };
    var snapshot = silent ? _snapshotValidityClasses(control) : null;
    var result = await element.save(name, control, repo);
    if (snapshot) _restoreValidityClasses(snapshot);
    return result;
  }

  return {
    success: true,
    data: _collectFields(control),
    namespace: namespace,
    errors: []
  };
}

async function _collectDocument(wizard, silent) {
  var wizardId = wizard.getAttribute("id");
  var totalSteps = Number(wizard.getAttribute("data-wizard-steps"));
  var flat = {};
  var steps = [];

  for (var i = 1; i <= totalSteps; i++) {
    var stepSelector = '[data-wizard-id="' + wizardId + '"][data-wizard-step="' + i + '"]';
    var stepEl = document.querySelector(stepSelector);
    var stepData = {};
    var topLevelControls = _getTopLevelStepControls(stepEl);
    var hasTopLevelControls = topLevelControls.length > 0;

    if (hasTopLevelControls) {
      try {
        for (var c = 0; c < topLevelControls.length; c++) {
          var control = topLevelControls[c];
          var controlName = control.getAttribute("name");
          if (!controlName) continue;

          var ctrl = await _saveTopLevelControl(control, silent);
          if (ctrl && ctrl.data !== null && ctrl.data !== undefined) {
            stepData[controlName] = ctrl.data;
          }
        }
      } catch (e) {
        // fallback for this step
        if (stepEl) stepData = _collectFields(stepEl);
      }
    } else {
      // No top-level UAT controls found — use raw field scrape for this step
      if (stepEl) stepData = _collectFields(stepEl);
    }

    steps.push({
      step: i,
      data: stepData,
      controls: _extractControlMeta(stepEl)
    });
    Object.assign(flat, stepData);
  }

  return { flat: flat, steps: steps };
}

// ─── Field collection (fallback / draft capture) ──────────────────────────────

function _collectFields(container) {
  var data = {};
  var inputs = container.querySelectorAll("input, select, textarea");

  // Helper: resolve field name from own attribute or nearest named ancestor
  function _resolveName(input) {
    var n = input.getAttribute("name");
    if (!n) {
      var p = input.closest("[name]");
      if (p) n = p.getAttribute("name");
    }
    return n || null;
  }

  /* A multi-value group is inputs that carry the SAME OWN `name` attribute.
   *
   * Previously this counted RESOLVED names, which include the name inherited
   * from an ancestor `uae[name]`. Every composite control's inner inputs are
   * identified by `id` and carry no `name` of their own, so they all resolved to
   * the wrapper's single name and were mistaken for a multi-value group —
   * collapsing the whole control into an array of whichever inner inputs happened
   * to be non-empty.
   *
   * Observed on the Exploit preorder wizard: `ua.address` has six inner inputs
   * (line-1, line-2, city, state, country, postalcode), only the readonly country
   * input has a value on a fresh form, so the draft persisted
   *   shipping: ["United States"]
   * instead of the address object. On the next page load `ua.address.load()` was
   * handed that array, `sanitize.address()` rejected it (no `line1` key), and it
   * threw "Invalid Address Data." — once per clean load, and the buyer's typed
   * address was silently lost from the draft.
   *
   * Counting OWN names is the correct rule and preserves the real multi-value
   * case this branch exists for: `ua.tags` builds its hidden inputs with an
   * explicit `hidden.name = name` (ua.tags.js:289), so those still group. No UAT
   * element emits inner inputs with an explicit shared `name` other than tags,
   * and radio/checkbox groups are handled by their own branches below. */
  var nameCounts = {};
  for (var p = 0; p < inputs.length; p++) {
    var ownName = inputs[p].getAttribute("name");
    if (ownName) nameCounts[ownName] = (nameCounts[ownName] || 0) + 1;
  }

  var multiValues = {};  // collected values for names that appear more than once

  for (var i = 0; i < inputs.length; i++) {
    var input = inputs[i];
    var fieldName = _resolveName(input);
    if (!fieldName) continue;

    var inputType = (input.type || "").toLowerCase();
    var isMulti = nameCounts[fieldName] > 1;

    if (inputType === "radio") {
      if (input.checked) data[fieldName] = input.value;
      continue;
    }

    if (inputType === "checkbox") {
      if (isMulti) {
        // Multiple checkboxes with same name → array of selected values
        if (!multiValues[fieldName]) multiValues[fieldName] = [];
        if (input.checked) {
          multiValues[fieldName].push(input.value === "on" ? true : input.value);
        }
      } else {
        // Single checkbox → plain boolean
        data[fieldName] = input.checked;
      }
      continue;
    }

    // Text, hidden, select, textarea, etc.
    if (isMulti) {
      // Multiple inputs with same name (e.g. ua.tags hidden inputs) → array
      if (!multiValues[fieldName]) multiValues[fieldName] = [];
      if (input.value) multiValues[fieldName].push(input.value);
    } else {
      data[fieldName] = input.value;
    }
  }

  // Merge multi-value groups into data
  for (var mName in multiValues) {
    data[mName] = multiValues[mName];
  }

  return data;
}

// ─── Validation ───────────────────────────────────────────────────────────────

async function _validateStep(wizard, stepNum) {
  var wizardId = wizard.getAttribute("id");
  var stepSelector = '[data-wizard-id="' + wizardId + '"][data-wizard-step="' + stepNum + '"]';
  var stepEl = document.querySelector(stepSelector);
  if (!stepEl) return { success: true };

  // Try UATools.save for full UA control validation (triggers per-element validation)
  if (window.UATools && typeof window.UATools.save === "function") {
    try {
      var result = await window.UATools.save(stepSelector, true);
      if (result && typeof result.success === "boolean") return result;
    } catch (e) { /* fall through to manual */ }
  }

  // Manual fallback: check [required] form inputs
  var required = stepEl.querySelectorAll("input[required], select[required], textarea[required]");
  var invalid = false;
  var manualDoc = { success: true };
  for (var r = 0; r < required.length; r++) {
    var field = required[r];
    var isCheckbox = (field.type || "").toLowerCase() === "checkbox";
    var isEmpty = isCheckbox ? !field.checked : !(field.value || "").toString().trim();
    if (isEmpty) {
      field.classList.add("is-invalid");
      field.classList.remove("is-valid");
      invalid = true;
      // Try to find a human-readable label for the error message
      var nameEl = field.closest("[name]");
      var label = (nameEl && nameEl.getAttribute("name")) || field.getAttribute("id") || "field";
      if (!manualDoc[label]) {
        manualDoc[label] = { success: false, errors: [], namespace: "ua.wizard.validation" };
      }
      manualDoc[label].errors.push({
        type: "validation",
        message: label + " is required."
      });
    } else {
      field.classList.remove("is-invalid");
      field.classList.add("is-valid");
    }
  }
  if (invalid) {
    manualDoc.success = false;
    return manualDoc;
  }
  return { success: true };
}

function _escapeHtml(str) {
  return (str || "").toString()
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function _toTitleCaseLabel(key) {
  var txt = (key || "").toString().replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
  if (!txt) return "";
  return txt.charAt(0).toUpperCase() + txt.slice(1);
}

function _isSensitiveField(key, allowPasswordMask) {
  var txt = (key || "").toString().toLowerCase();
  var sensitiveRegex = allowPasswordMask
    ? /secret|token|apikey|api_key|privatekey|private_key|passkey|challenge|credential|signature/
    : /password|secret|token|apikey|api_key|privatekey|private_key|passkey|challenge|credential|signature/;
  return sensitiveRegex.test(txt);
}

function _formatSummaryValue(value) {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return String(value);
  if (typeof value === "string") return value;
  if (value instanceof Array) {
    var mapped = value.map(function(item) {
      if (item === null || item === undefined) return "";
      if (typeof item === "object") {
        try {
          return JSON.stringify(item);
        } catch (error) {
          return String(item);
        }
      }
      return String(item);
    }).filter(function(item) { return item !== ""; });
    return mapped.join(", ");
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value, null, 2);
    } catch (error) {
      return String(value);
    }
  }
  return String(value);
}

function _parseCsvFields(csv) {
  var txt = (csv || "").toString().trim();
  if (!txt) return [];
  return txt.split(",").map(function(item) {
    return item.toString().trim();
  }).filter(function(item) {
    return !!item;
  });
}

function _pickFields(data, allowList, allowPasswordMask) {
  if (!(data && typeof data === "object")) return {};
  var selected = {};
  if (allowList instanceof Array && allowList.length > 0) {
    for (var i = 0; i < allowList.length; i++) {
      var key = allowList[i];
      if (_isSensitiveField(key, allowPasswordMask)) continue;
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        selected[key] = data[key];
      }
    }
    return selected;
  }

  for (var k in data) {
    if (!Object.prototype.hasOwnProperty.call(data, k)) continue;
    if (_isSensitiveField(k, allowPasswordMask)) continue;
    if (k === "confirmation" || k === "redirect" || k === "message" || k === "success") continue;
    selected[k] = data[k];
  }
  return selected;
}

function _toSummaryRows(data) {
  var rows = [];
  if (!(data && typeof data === "object")) return rows;
  for (var key in data) {
    if (!Object.prototype.hasOwnProperty.call(data, key)) continue;
    var formatted = _formatSummaryValue(data[key]);
    if (!formatted) continue;
    rows.push({
      label: _toTitleCaseLabel(key),
      value: formatted
    });
  }
  return rows;
}

function _extractNestedSuccessfulPayload(payload, depth, seen) {
  if (!(payload) || typeof payload !== "object") return null;
  if (depth > 8) return null;
  if (seen.indexOf(payload) >= 0) return null;
  seen.push(payload);

  var hasOutcome = !!(
    _safeText(payload.redirect) ||
    _safeText(payload.redirectUrl) ||
    _safeText(payload.reviewUrl) ||
    _safeText(payload.detailUrl) ||
    (payload.record && typeof payload.record === "object") ||
    _safeText(payload.successTitle) ||
    _safeText(payload.successDescription)
  );
  if (hasOutcome) return payload;

  var keys = ["data", "result", "response", "payload"];
  for (var i = 0; i < keys.length; i++) {
    var next = payload[keys[i]];
    if (!(next) || typeof next !== "object") continue;
    var resolved = _extractNestedSuccessfulPayload(next, depth + 1, seen);
    if (resolved) return resolved;
  }

  return null;
}

function _normalizeSuccessfulPayload(result) {
  var payload = result || {};
  var depth = 0;

  while (
    payload &&
    payload.success === true &&
    Object.prototype.hasOwnProperty.call(payload, "data") &&
    payload.data &&
    typeof payload.data === "object" &&
    depth < 8
  ) {
    payload = payload.data;
    depth += 1;
  }

  var extracted = _extractNestedSuccessfulPayload(payload, 0, []);
  return extracted || payload || {};
}

function _resolveSuccessNavigationTarget(payload, fallbackPayload) {
  var source = payload && typeof payload === "object" ? payload : {};
  var fallback = fallbackPayload && typeof fallbackPayload === "object" ? fallbackPayload : {};
  return (
    _safeText(source.redirect) ||
    _safeText(source.redirectUrl) ||
    _safeText(source.detailUrl) ||
    _safeText(source.reviewUrl) ||
    _safeText(fallback.redirect) ||
    _safeText(fallback.redirectUrl) ||
    _safeText(fallback.detailUrl) ||
    _safeText(fallback.reviewUrl) ||
    ""
  );
}

function _buildSuccessActions(payload) {
  var source = payload && typeof payload === "object" ? payload : {};
  var candidates = [
    { href: source.redirect, label: "Continue" },
    { href: source.redirectUrl, label: "Continue" },
    { href: source.detailUrl, label: "View details" },
    { href: source.reviewUrl, label: "Review" }
  ];
  var actions = [];
  var seen = {};

  for (var i = 0; i < candidates.length; i++) {
    var href = _safeText(candidates[i].href);
    if (!href || seen[href]) continue;
    seen[href] = true;
    actions.push({
      href: href,
      label: candidates[i].label
    });
  }

  return actions;
}


function _setWizardNavigationState(wizard, disabled) {
  if (!wizard) return;
  var wizardId = wizard.getAttribute("id");
  var continueBtn = document.getElementById(wizardId + "-continue");
  var backBtn = document.getElementById(wizardId + "-back");
  if (continueBtn) continueBtn.disabled = !!disabled;
  if (backBtn) backBtn.disabled = !!disabled;
}

function _isLikelyRouteTarget(value) {
  var target = (value || "").toString().trim();
  if (!target) return false;
  if (target.indexOf("/") === 0) return true;
  if (/^https?:\/\//i.test(target)) return true;
  return false;
}

function _findParentWizard(wizard) {
  if (!(wizard && wizard.parentElement && wizard.parentElement.closest)) return null;
  var parent = wizard.parentElement.closest("[data-wizard='true']");
  if (parent === wizard) return null;
  return parent || null;
}

function _shouldDelegateNestedSubmitToParent(wizard) {
  if (!wizard || !wizard.closest) return false;
  var parentWizard = _findParentWizard(wizard);
  if (!parentWizard) return false;

  var uaeEl = wizard.closest("uae");
  var target = uaeEl ? (uaeEl.getAttribute("name") || "") : "";
  return !_isLikelyRouteTarget(target);
}

async function _delegateNestedSubmitToParent(wizard) {
  var parentWizard = _findParentWizard(wizard);
  if (!parentWizard) return false;

  var parentId = parentWizard.getAttribute("id") || "";
  if (!parentId) return false;

  var parentContinueBtn = document.getElementById(parentId + "-continue");
  if (!parentContinueBtn || parentContinueBtn.disabled) return false;

  parentContinueBtn.click();
  return true;
}

function _safeText(value) {
  return (value || "").toString().trim();
}

function _isEditOptions(options) {
  if (!(options) || typeof options !== "object") return false;
  if (options.editing === true) return true;
  if (_safeText(options.id)) return true;
  if (_safeText(options.recordId)) return true;
  if (options.document && typeof options.document === "object" && _safeText(options.document.id)) return true;
  return false;
}

function _isLikelyEditingWizard(wizard) {
  if (!wizard) return false;
  if (wizard.getAttribute("data-wizard-editing") === "true") return true;
  if (_safeText(wizard.getAttribute("data-record-id"))) return true;
  if (_safeText(wizard.getAttribute("data-wizard-record-id"))) return true;

  var candidates = wizard.querySelectorAll('input[type="hidden"][name="id"], input[type="hidden"][name$=".id"], input[type="hidden"][name$="Id"], input[type="hidden"][data-wizard-record-id]');
  for (var i = 0; i < candidates.length; i++) {
    if (_safeText(candidates[i].value || candidates[i].getAttribute("value"))) {
      return true;
    }
  }
  return false;
}

function _setWizardStepJumpState(wizard, allowJump) {
  if (!wizard) return;
  var shouldAllow = allowJump === true;
  wizard.setAttribute("data-wizard-nav-free", shouldAllow ? "true" : "false");
  if (shouldAllow) {
    var totalSteps = Number(wizard.getAttribute("data-wizard-steps") || "1");
    var allSeen = [];
    for (var i = 1; i <= totalSteps; i++) {
      allSeen.push(i);
    }
    wizard.setAttribute("data-wizard-hasSeen", JSON.stringify(allSeen));
    return;
  }

  var currentStep = Number(wizard.getAttribute("data-wizard-current") || "1");
  var hasSeen = [];
  try {
    hasSeen = JSON.parse(wizard.getAttribute("data-wizard-hasSeen") || "[1]");
  } catch (error) {
    hasSeen = [1];
  }
  var nextSeen = [];
  for (var j = 0; j < hasSeen.length; j++) {
    var step = Number(hasSeen[j] || 0);
    if (step > 0 && step <= currentStep && nextSeen.indexOf(step) < 0) {
      nextSeen.push(step);
    }
  }
  if (nextSeen.indexOf(currentStep) < 0) nextSeen.push(currentStep);
  if (nextSeen.indexOf(1) < 0) nextSeen.unshift(1);
  wizard.setAttribute("data-wizard-hasSeen", JSON.stringify(nextSeen));
}

function _getWizardStepElements(wizard) {
  if (!wizard) return [];

  var body = wizard.querySelector(":scope > .wizard") || wizard.querySelector(".wizard");
  if (!body) return [];

  var steps = body.children || [];
  var out = [];
  for (var i = 0; i < steps.length; i++) {
    var stepEl = steps[i];
    if (!(stepEl && stepEl.getAttribute)) continue;
    if (!stepEl.hasAttribute("data-wizard-step")) continue;
    if (stepEl.closest("[data-wizard='true']") !== wizard) continue;
    out.push(stepEl);
  }
  return out;
}

function _isWizardStepVisible(stepEl) {
  if (!stepEl || !stepEl.getAttribute) return false;
  if (stepEl.getAttribute("data-wizard-step-hidden") === "true") return false;
  return stepEl.getAttribute("data-fse-step-hidden") !== "true";
}

function _getVisibleWizardSteps(wizard) {
  var steps = _getWizardStepElements(wizard);
  var visible = [];
  for (var i = 0; i < steps.length; i++) {
    if (!_isWizardStepVisible(steps[i])) continue;
    var stepNum = Number(steps[i].getAttribute("data-wizard-step") || "0");
    if (stepNum > 0) visible.push(stepNum);
  }
  return visible;
}

function _getVisibleWizardStepNumber(wizard, stepNum) {
  var visibleSteps = _getVisibleWizardSteps(wizard);
  var visibleIndex = visibleSteps.indexOf(Number(stepNum || 0));
  if (visibleIndex >= 0) return visibleIndex + 1;
  return Number(stepNum || 0);
}

function _syncWizardVisibleStepState(wizard) {
  if (!wizard) return;

  var visibleSteps = _getVisibleWizardSteps(wizard);
  var currentStep = Number(wizard.getAttribute("data-wizard-current") || "1");
  var nextCurrentStep = currentStep;

  if (visibleSteps.length > 0 && visibleSteps.indexOf(currentStep) < 0) {
    nextCurrentStep = visibleSteps[0];
    for (var i = 0; i < visibleSteps.length; i++) {
      nextCurrentStep = visibleSteps[i];
      if (visibleSteps[i] > currentStep) break;
    }
  }

  var steps = _getWizardStepElements(wizard);
  for (var s = 0; s < steps.length; s++) {
    var stepEl = steps[s];
    var stepNum = Number(stepEl.getAttribute("data-wizard-step") || "0");
    var isVisibleStep = visibleSteps.indexOf(stepNum) >= 0;
    if (!isVisibleStep || stepNum !== nextCurrentStep) stepEl.classList.add("d-none");
    else stepEl.classList.remove("d-none");
  }

  wizard.setAttribute("data-wizard-current", String(nextCurrentStep));

  var wizardId = wizard.getAttribute("id");
  var visibleIndex = visibleSteps.indexOf(nextCurrentStep);
  var lastVisibleStep = visibleSteps.length > 0 ? visibleSteps[visibleSteps.length - 1] : nextCurrentStep;

  var backBtn = document.getElementById(wizardId + "-back");
  if (backBtn) {
    if (visibleIndex > 0) backBtn.classList.remove("d-none");
    else backBtn.classList.add("d-none");
  }

  var continueBtn = document.getElementById(wizardId + "-continue");
  if (continueBtn) {
    var continueText = (wizard.getAttribute("data-wizard-continue-text") || "").toString();
    var submitText = (wizard.getAttribute("data-wizard-submit-text") || "").toString();
    if (nextCurrentStep === lastVisibleStep) {
      if (submitText.length > 0) {
        continueBtn.innerHTML = '<span class="d-lg-inline">' + _escapeHtml(submitText) + '&nbsp;</span><i class="bi bi-check2"></i>';
      } else {
        continueBtn.innerHTML = '<i class="bi bi-check2"></i><span class="d-none d-lg-inline"> Submit</span>';
      }
    } else if (continueText.length > 0) {
      continueBtn.innerHTML = '<span class="d-lg-inline">' + _escapeHtml(continueText) + '&nbsp;</span><i class="bi bi-arrow-right"></i>';
    } else {
      continueBtn.innerHTML = '<i class="bi bi-arrow-right"></i><span class="d-none d-lg-inline"> Continue</span>';
    }
  }

  var progress = wizard.querySelector('[data-wizard-progress="true"]');
  if (progress) {
    var denominator = visibleSteps.length || 1;
    var numerator = visibleIndex >= 0 ? (visibleIndex + 1) : 1;
    progress.style.width = (numerator === denominator ? 100 : (numerator / denominator) * 100) + "%";
  }

  _updateStepNavigationState(wizard);
}

function _updateStepNavigationState(wizard) {
  if (!wizard || wizard.getAttribute("data-wizard-show-nav") !== "true") return;

  var currentStep = Number(wizard.getAttribute("data-wizard-current") || "1");
  var visibleSteps = _getVisibleWizardSteps(wizard);
  var allowStepJump = wizard.getAttribute("data-wizard-nav-free") === "true";
  var hasSeen = [];
  try {
    hasSeen = JSON.parse(wizard.getAttribute("data-wizard-hasSeen") || "[1]");
  } catch (error) {
    hasSeen = [1];
  }

  var navButtons = wizard.querySelectorAll("[data-wizard-nav-step]");
  for (var i = 0; i < navButtons.length; i++) {
    var button = navButtons[i];
    var targetStep = Number(button.getAttribute("data-wizard-nav-step") || "0");
    var visibleIndex = visibleSteps.indexOf(targetStep);
    var targetTitle = (button.getAttribute("data-wizard-nav-title") || ("Step " + targetStep)).toString().trim();
    var isSeen = allowStepJump ? true : (hasSeen.indexOf(targetStep) >= 0);
    var isCurrent = targetStep === currentStep;
    var displayStep = visibleIndex >= 0 ? (visibleIndex + 1) : targetStep;
    button.setAttribute("title", "Step " + displayStep + ": " + targetTitle);
    button.setAttribute("aria-label", "Step " + displayStep + ": " + targetTitle);
    button.setAttribute("data-wizard-visible-step", visibleIndex >= 0 ? String(displayStep) : "");

    var pill = button.querySelector(".ua-wizard-step-pill");
    if (pill) pill.textContent = String(displayStep);

    button.classList.remove("active");
    if (visibleIndex < 0) {
      button.classList.add("d-none");
      button.setAttribute("hidden", "hidden");
      button.setAttribute("aria-hidden", "true");
      button.setAttribute("tabindex", "-1");
      button.disabled = true;
      button.classList.remove("opacity-50");
      continue;
    }
    button.classList.remove("d-none");
    button.removeAttribute("hidden");
    button.removeAttribute("aria-hidden");
    button.removeAttribute("tabindex");
    if (!isSeen) {
      button.disabled = true;
      button.classList.add("opacity-50");
      continue;
    }

    button.disabled = false;
    button.classList.remove("opacity-50");
    if (isCurrent) {
      button.classList.add("active");
    }
  }
}

function _collectWizardStepTitles(wizard) {
  var wizardId = wizard.getAttribute("id");
  var totalSteps = Number(wizard.getAttribute("data-wizard-steps") || "1");
  var titles = [];

  for (var i = 1; i <= totalSteps; i++) {
    var stepEl = wizard.querySelector('[data-wizard-id="' + wizardId + '"][data-wizard-step="' + i + '"]');
    if (!stepEl) {
      titles.push("Step " + i);
      continue;
    }

    var explicit = stepEl.querySelector("[data-wizard-title]");
    if (explicit) {
      var titleText = (explicit.getAttribute("data-wizard-title") || "").toString().trim();
      if (titleText) {
        titles.push(titleText);
        continue;
      }
    }
    titles.push("Step " + i);
  }

  return titles;
}

function _buildStepPill(stepNum) {
  return {
    n: "span",
    c: "ua-wizard-step-pill",
    i: String(stepNum)
  };
}

function _buildStepLabel(stepNum, title) {
  return [
    _buildStepPill(stepNum),
    {
      n: "span",
      c: "ua-wizard-step-label",
      i: (title || "").toString()
    }
  ];
}

function _buildContinueButtonInner(continueText) {
  if (continueText && continueText.length > 0) {
    return [
      { "n": "span", "c": "d-lg-inline", "i": continueText + "\u00a0" },
      { "n": "i", "c": "bi bi-arrow-right" }
    ];
  }
  return [
    { "n": "i", "c": "bi bi-arrow-right" },
    { "n": "span", "c": "d-none d-lg-inline", "i": " Continue" }
  ];
}

function _buildSubmitButtonInner(submitText) {
  if (submitText && submitText.length > 0) {
    return [
      { "n": "span", "c": "d-lg-inline", "i": submitText + "\u00a0" },
      { "n": "i", "c": "bi bi-check2" }
    ];
  }
  return [
    { "n": "i", "c": "bi bi-check2" },
    { "n": "span", "c": "d-none d-lg-inline", "i": " Submit" }
  ];
}

function _buildStepHeaderDoc(headerOptions, fallbackTitle) {
  if (!(headerOptions && typeof headerOptions === "object")) return null;

  var title = (headerOptions.title || fallbackTitle || "").toString().trim();
  if (!title) return null;

  var description = (headerOptions.description || "").toString();
  var hints = headerOptions.hints;
  var inner = null;
  if (hints) {
    inner = {
      n: "div",
      c: "small text-muted mt-2",
      i: hints
    };
  }

  return {
    n: "ua.header",
    icon: (headerOptions.icon || "diagram-3").toString(),
    title: title,
    description: description,
    inner: inner
  };
}

function _extractControlMeta(stepEl) {
  var controls = {};
  if (!stepEl) return controls;

  var uaeControls = stepEl.querySelectorAll("uae[name][namespace]");
  for (var i = 0; i < uaeControls.length; i++) {
    var control = uaeControls[i];
    var fieldName = control.getAttribute("name");
    if (!fieldName) continue;

    var controlNamespace = (control.getAttribute("namespace") || "").toString().trim();
    var controlElement = controlNamespace ? jsonRender.search(controlNamespace) : null;
    var previewNamespace = "";
    if (controlElement && controlElement.define && controlElement.define.preview) {
      previewNamespace = controlElement.define.preview.toString().trim();
    }

    var attrs = {};
    if (control.attributes) {
      for (var ai = 0; ai < control.attributes.length; ai++) {
        var attr = control.attributes[ai];
        attrs[attr.name] = attr.value;
      }
    }

    var input = control.querySelector("input, select, textarea");
    if (input && input.getAttribute("type")) {
      attrs.controlType = input.getAttribute("type");
    }

    controls[fieldName] = {
      namespace: controlNamespace,
      preview: previewNamespace,
      attributes: attrs,
      title: _controlTitle(control),
      skipSummary: control.getAttribute("summary") === "false" || !!control.querySelector("[summary='false']"),
      optionLabels: _controlOptionLabels(control)
    };
  }
  return controls;
}

/* The question a person saw for this control: its title attribute, else the
 * text of its first label. Lets the summary read "How long in business?"
 * instead of the storage key "InBusiness". */
function _controlTitle(control) {
  var title = (control.getAttribute("title") || "").toString().trim();
  if (title) return title;
  var titled = control.querySelector("[title]");
  if (titled) title = (titled.getAttribute("title") || "").toString().trim();
  if (title) return title;
  var label = control.querySelector("label");
  return label ? (label.textContent || "").replace(/\s+/g, " ").trim() : "";
}

/* value -> visible label for every choice the control drew (radios, checkboxes,
 * <option>s), so previews can show "Less than 1 year" instead of "under-1yr". */
function _controlOptionLabels(control) {
  var map = {};
  var choices = control.querySelectorAll("input[type='radio'][value], input[type='checkbox'][value]");
  for (var i = 0; i < choices.length; i++) {
    var choice = choices[i];
    var value = choice.getAttribute("value");
    if (!value || value === "on" || Object.prototype.hasOwnProperty.call(map, value)) continue;
    var label = null;
    if (choice.id) label = control.querySelector("label[for='" + choice.id.replace(/'/g, "\\'") + "']");
    if (!label) label = choice.closest("label");
    var text = label ? (label.textContent || "").replace(/\s+/g, " ").trim() : "";
    if (text) map[value] = text;
  }
  var opts = control.querySelectorAll("select option[value]");
  for (var j = 0; j < opts.length; j++) {
    var v = opts[j].getAttribute("value");
    var t = (opts[j].textContent || "").replace(/\s+/g, " ").trim();
    if (v && t && !Object.prototype.hasOwnProperty.call(map, v)) map[v] = t;
  }
  return map;
}

function _toSummaryItems(stepInfo, includeSensitiveForMasking) {
  var items = [];
  var stepData = (stepInfo && stepInfo.data && typeof stepInfo.data === "object") ? stepInfo.data : {};
  var controls = (stepInfo && stepInfo.controls && typeof stepInfo.controls === "object") ? stepInfo.controls : {};
  var keys = Object.keys(stepData);

  for (var i = 0; i < keys.length; i++) {
    var key = keys[i];
    if (_isSensitiveField(key, includeSensitiveForMasking)) continue;
    // A control can opt out of the read-back (e.g. a one-time verification
    // code): set `summary: "false"` on it.
    var meta = controls[key];
    if (meta && meta.skipSummary) continue;
    items.push({
      key: key,
      label: (controls[key] && controls[key].title) || _toTitleCaseLabel(key),
      value: stepData[key],
      control: controls[key] || {}
    });
  }

  return items;
}

function _fallbackPreviewText(item) {
  var key = (item.key || "").toString().toLowerCase();
  var value = _formatSummaryValue(item.value);
  if (/password/.test(key)) {
    var length = (value || "").length;
    if (length <= 0) return "";
    return new Array(length + 1).join("\u2022");
  }
  return value;
}

async function _renderPreviewHtml(item, mode) {
  var control = item.control || {};
  var previewNamespace = (control.preview || "").toString().trim();
  if (!previewNamespace && control.namespace) {
    var controlElement = jsonRender.search(control.namespace);
    if (controlElement && controlElement.define && controlElement.define.preview) {
      previewNamespace = (controlElement.define.preview || "").toString().trim();
    }
  }
  if (!previewNamespace) {
    previewNamespace = "ua.preview.text";
  }

  var options = {
    n: previewNamespace,
    value: item.value,
    name: item.key,
    label: item.label,
    mode: mode || "summary",
    controlNamespace: control.namespace || "",
    optionLabels: control.optionLabels || {}
  };

  if (control.attributes && typeof control.attributes === "object") {
    options.controlAttributes = { ...control.attributes };
    if (control.attributes.controlType) {
      options.controlType = control.attributes.controlType;
      options.type = control.attributes.controlType;
    }
  }

  if (!(window.UATools && typeof window.UATools.render === "function")) {
    return "<span class='ua-control-preview-text'>" + _escapeHtml(_fallbackPreviewText(item)) + "</span>";
  }

  try {
    var rendered = await window.UATools.render(options);
    if (rendered && rendered.nodeType) {
      var holder = document.createElement("div");
      holder.appendChild(rendered);
      return holder.innerHTML;
    }
  } catch (error) {
    console.warn("Wizard preview render failed", {
      previewNamespace: previewNamespace,
      field: item.key,
      error: error
    });
  }

  return "<span class='ua-control-preview-text'>" + _escapeHtml(_fallbackPreviewText(item)) + "</span>";
}

function _renderSummaryRow(title, fieldsHtml, visibleStepNum, editable, targetStepNum) {
  var goToStep = Number(targetStepNum || visibleStepNum || 0);
  var safeVisibleStepNum = Number(visibleStepNum || goToStep || 0);
  var buttonLabel = "Edit Step " + safeVisibleStepNum + ": " + (title || "Step " + safeVisibleStepNum);
  var button = editable
    ? "<button type='button' class='btn btn-outline-secondary btn-sm ua-wizard-summary-edit' data-wizard-summary-goto='" + goToStep + "' data-wizard-summary-visible-step='" + _escapeHtml(safeVisibleStepNum) + "' title='" + _escapeHtml(buttonLabel) + "' aria-label='" + _escapeHtml(buttonLabel) + "'><i class='bi bi-pencil-square'></i><span class='ms-1'>Edit</span></button>"
    : "";
  return [
    "<section class='ua-wizard-summary-row ua-wizard-success-section'>",
    "<div class='ua-wizard-summary-row-header d-flex justify-content-between align-items-start gap-2'>",
    "<h4 class='ua-wizard-success-section-title d-inline-flex align-items-center flex-wrap gap-2 mb-0'><span class='ua-wizard-step-pill'>" + _escapeHtml(visibleStepNum) + "</span><span>" + _escapeHtml(title) + "</span></h4>",
    button,
    "</div>",
    "<div class='ua-wizard-summary-row-body small mt-3'>" + fieldsHtml + "</div>",
    "</section>"
  ].join("");
}

async function _renderSummaryFields(stepInfo, mode) {
  var items = _toSummaryItems(stepInfo, true);
  if (!(items instanceof Array) || items.length === 0) {
    // Every field on this step opted out of the read-back (summary="false"):
    // say nothing rather than "No values entered." for a step the person did fill.
    var controls = (stepInfo && stepInfo.controls) || {};
    var keys = Object.keys((stepInfo && stepInfo.data) || {});
    if (keys.length > 0 && keys.every(function(k) { return controls[k] && controls[k].skipSummary; })) return null;
    return "No values entered.";
  }

  var rows = [];
  for (var i = 0; i < items.length; i++) {
    var item = items[i];
    var previewHtml = await _renderPreviewHtml(item, mode || "summary");
    rows.push("<div class='ua-wizard-success-item mb-2'><div class='ua-wizard-success-item-label'>" + _escapeHtml(item.label) + "</div><div class='ua-wizard-success-item-value'>" + previewHtml + "</div></div>");
  }
  return rows.join("");
}

async function _renderPreSubmitSummary(wizard) {
  if (!wizard || wizard.getAttribute("data-wizard-summary-step") !== "true") return;

  var currentStep = Number(wizard.getAttribute("data-wizard-current") || "1");
  var totalSteps = Number(wizard.getAttribute("data-wizard-steps") || "1");
  var visibleSteps = _getVisibleWizardSteps(wizard);
  var lastVisibleStep = visibleSteps.length > 0 ? visibleSteps[visibleSteps.length - 1] : totalSteps;
  if (currentStep !== lastVisibleStep || lastVisibleStep <= 1) return;

  var summaryHost = wizard.querySelector("[data-wizard-summary-host='true']");
  if (!summaryHost) return;

  var doc = await _collectDocument(wizard, true);
  var titles = _collectWizardStepTitles(wizard);

  var html = [];
  for (var i = 0; i < doc.steps.length; i++) {
    var stepInfo = doc.steps[i] || {};
    if (stepInfo.step === totalSteps) continue;
    if (visibleSteps.indexOf(stepInfo.step) < 0) continue;
    var title = titles[(stepInfo.step || 1) - 1] || ("Step " + stepInfo.step);
    var fieldsHtml = await _renderSummaryFields(stepInfo, "summary");
    if (fieldsHtml === null) continue;
    html.push(_renderSummaryRow(title, fieldsHtml, _getVisibleWizardStepNumber(wizard, stepInfo.step), true, stepInfo.step));
  }

  if (html.length === 0) {
    summaryHost.innerHTML = "<p class='text-muted mb-0'>No entered values to review yet.</p>";
  } else {
    summaryHost.innerHTML = html.join("");
  }
}

function _deriveStepTitle(rawStep, stepNum) {
  if (rawStep && typeof rawStep === "object" && rawStep["data-wizard-title"]) {
    var title = (rawStep["data-wizard-title"] || "").toString().trim();
    if (title) return title;
  }
  return "Step " + stepNum;
}

function _pickDynamicSteps(result, pathHint) {
  if (!result || typeof result !== "object") return [];

  var path = (pathHint || "auto").toString().trim();
  var candidate = null;

  if (path && path !== "auto") {
    if (path === "steps") candidate = result.steps;
    else if (path === "dynamicSteps") candidate = result.dynamicSteps;
    else if (path === "data.steps") candidate = result.data && result.data.steps;
    else if (path === "data.dynamicSteps") candidate = result.data && result.data.dynamicSteps;
  } else {
    candidate = result.steps
      || result.dynamicSteps
      || (result.data && result.data.steps)
      || (result.data && result.data.dynamicSteps);
  }

  return candidate instanceof Array ? candidate : [];
}

async function _appendDynamicSteps(wizard, rawSteps) {
  if (!(rawSteps instanceof Array) || rawSteps.length === 0) return 0;
  if (!(window.UATools && typeof window.UATools.render === "function")) return 0;

  var wizardId = wizard.getAttribute("id");
  var body = wizard.querySelector(".wizard");
  if (!body) return 0;

  var totalSteps = Number(wizard.getAttribute("data-wizard-steps") || "1");
  var added = 0;

  for (var i = 0; i < rawSteps.length; i++) {
    var nextStep = totalSteps + 1;
    var rawStep = rawSteps[i];
    var stepDoc = {
      n: "div",
      "data-wizard-id": wizardId,
      "data-wizard-step": nextStep,
      c: "wizard-step d-none",
      i: rawStep
    };

    var stepEl = await window.UATools.render(stepDoc);
    body.appendChild(stepEl);

    var nav = wizard.querySelector("[data-wizard-step-nav='true']");
    if (nav) {
      var navTitle = _deriveStepTitle(rawStep, nextStep);
      var navBtnDoc = {
        n: "button",
        type: "button",
        c: "btn btn-sm btn-outline-secondary me-2 mb-2 d-none",
        "data-wizard-nav-step": String(nextStep),
        "data-wizard-nav-title": navTitle,
        "data-wizard-id": wizardId,
        title: navTitle,
        i: _buildStepLabel(nextStep, navTitle)
      };
      var navBtn = await window.UATools.render(navBtnDoc);
      nav.appendChild(navBtn);
    }

    totalSteps = nextStep;
    added++;
  }

  wizard.setAttribute("data-wizard-steps", String(totalSteps));

  if (window.UATools && typeof window.UATools.after === "function") {
    window.UATools.after();
  }

  _updateStepNavigationState(wizard);
  return added;
}

async function _loadDynamicStepsIfNeeded(wizard, currentStep) {
  var url = (wizard.getAttribute("data-wizard-dynamic-url") || "").toString().trim();
  if (!url) return 0;

  var loaded = [];
  try {
    loaded = JSON.parse(wizard.getAttribute("data-wizard-dynamic-loaded") || "[]");
    if (!(loaded instanceof Array)) loaded = [];
  } catch (error) {
    loaded = [];
  }
  if (loaded.indexOf(currentStep) >= 0) return 0;

  var doc = await _collectDocument(wizard, true);
  var pathHint = (wizard.getAttribute("data-wizard-dynamic-path") || "auto").toString();
  var method = (wizard.getAttribute("data-wizard-dynamic-method") || "POST").toString().trim().toUpperCase() || "POST";
  var wizardId = wizard.getAttribute("id");
  var totalSteps = Number(wizard.getAttribute("data-wizard-steps") || "1");

  var payload = {
    action: "next-steps",
    document: doc.flat || {},
    steps: doc.steps || [],
    wizard: {
      id: wizardId,
      current: currentStep,
      total: totalSteps
    }
  };

  var response = await fetch(url, {
    method: method,
    headers: {
      "Content-Type": "application/json",
      "Accept": "application/json"
    },
    body: JSON.stringify(payload)
  });
  if (!response.ok) return 0;

  var result = await response.json();
  if (!(result && result.success !== false)) return 0;

  var steps = _pickDynamicSteps(result, pathHint);
  if (!(steps instanceof Array) || steps.length === 0) return 0;

  var added = await _appendDynamicSteps(wizard, steps);
  if (added > 0) {
    loaded.push(currentStep);
    wizard.setAttribute("data-wizard-dynamic-loaded", JSON.stringify(loaded));
  }

  return added;
}

function _setWizardCompleteState(wizard, completed, hideToolbar) {
  if (!wizard) return;
  wizard.setAttribute("data-wizard-complete", completed ? "true" : "false");
  _setWizardNavigationState(wizard, completed);
  if (completed && hideToolbar) {
    var footer = wizard.querySelector(".wizard-footer");
    if (footer) footer.classList.add("d-none");
  }
}

function _buildSuccessGroups(wizard, doc, allowList) {
  var groups = [];
  if (!(doc && doc.steps instanceof Array)) return groups;

  var titles = _collectWizardStepTitles(wizard);
  var totalSteps = Number(wizard.getAttribute("data-wizard-steps") || "1");
  var visibleSteps = _getVisibleWizardSteps(wizard);

  for (var i = 0; i < doc.steps.length; i++) {
    var stepInfo = doc.steps[i] || {};
    var stepNum = Number(stepInfo.step || 0);
    if (!stepNum) continue;
    if (wizard.getAttribute("data-wizard-summary-step") === "true" && stepNum === totalSteps) {
      continue;
    }

    var visibleIndex = visibleSteps.indexOf(stepNum);
    if (visibleIndex < 0) {
      continue;
    }
    var visibleStepNum = visibleIndex + 1;

    var selectedData = _pickFields(stepInfo.data || {}, allowList, true);
    var stepSelected = {
      step: stepNum,
      data: selectedData,
      controls: stepInfo.controls || {}
    };
    var items = _toSummaryItems(stepSelected, true);
    if (items.length === 0) continue;

    groups.push({
      step: visibleStepNum,
      title: titles[stepNum - 1] || ("Step " + visibleStepNum),
      items: items
    });
  }

  return groups;
}

function _buildSuccessConfirmationConfig(wizard, doc, payload) {
  var routeConfirmation = (payload && payload.confirmation && typeof payload.confirmation === "object")
    ? payload.confirmation
    : {};

  var successTitle = routeConfirmation.title
    || wizard.getAttribute("data-wizard-success-title")
    || "Success";
  var successDescription = routeConfirmation.description
    || payload.message
    || wizard.getAttribute("data-wizard-success-description")
    || "Your submission was completed successfully.";
  var successIcon = routeConfirmation.icon
    || wizard.getAttribute("data-wizard-success-icon")
    || "check2-circle";

  var hideToolbar = wizard.getAttribute("data-wizard-success-hide-toolbar") !== "false";
  if (typeof routeConfirmation.hideToolbar === "boolean") {
    hideToolbar = routeConfirmation.hideToolbar;
  }

  var showSent = wizard.getAttribute("data-wizard-success-show-sent") !== "false";
  if (wizard.getAttribute("data-wizard-success-show-document") === "false") {
    showSent = false;
  }
  if (typeof routeConfirmation.showSent === "boolean") {
    showSent = routeConfirmation.showSent;
  } else if (typeof routeConfirmation.showDocument === "boolean") {
    showSent = routeConfirmation.showDocument;
  }

  var showAppResponse = wizard.getAttribute("data-wizard-success-show-app-response") !== "false";
  if (wizard.getAttribute("data-wizard-success-show-response") === "false") {
    showAppResponse = false;
  }
  if (typeof routeConfirmation.showAppResponse === "boolean") {
    showAppResponse = routeConfirmation.showAppResponse;
  } else if (typeof routeConfirmation.showResponse === "boolean") {
    showAppResponse = routeConfirmation.showResponse;
  }

  var documentFieldList = _parseCsvFields(routeConfirmation.documentFields || wizard.getAttribute("data-wizard-success-document-fields") || "");
  var responseFieldList = _parseCsvFields(routeConfirmation.responseFields || wizard.getAttribute("data-wizard-success-response-fields") || "");

  var pickedDocument = _pickFields((doc && doc.flat) || {}, documentFieldList, true);
  var sentGroups = _buildSuccessGroups(wizard, doc || {}, documentFieldList);
  var sentRows = _toSummaryRows(pickedDocument);

  if (routeConfirmation.details instanceof Array) {
    for (var rd = 0; rd < routeConfirmation.details.length; rd++) {
      var detailRow = routeConfirmation.details[rd] || {};
      var detailLabel = (detailRow.label || detailRow.title || "").toString().trim();
      var detailValue = _formatSummaryValue(detailRow.value);
      if (!detailLabel || !detailValue) continue;
      sentRows.push({
        label: detailLabel,
        value: detailValue
      });
    }
  }

  var pickedResponse = _pickFields(payload || {}, responseFieldList);
  var responseRows = _toSummaryRows(pickedResponse);
  var actions = _buildSuccessActions(payload || {});

  return {
    title: successTitle,
    description: successDescription,
    icon: successIcon,
    hideToolbar: hideToolbar,
    showSent: showSent,
    showAppResponse: showAppResponse,
    sentGroups: sentGroups,
    sentRows: sentRows,
    responseRows: responseRows,
    actions: actions
  };
}

async function _buildSummarySectionHtml(title, rows) {
  if (!(rows instanceof Array) || rows.length === 0) return "";
  var items = [];
  for (var i = 0; i < rows.length; i++) {
    var row = rows[i] || {};
    var label = _escapeHtml(row.label || "");
    var value = (row.value || "").toString();
    if (!label || !value) continue;
    var previewItem = {
      key: row.label || ("row-" + i),
      label: row.label || "",
      value: value,
      control: {
        preview: "ua.preview.text",
        namespace: "ua.preview.text",
        attributes: {}
      }
    };
    var previewHtml = await _renderPreviewHtml(previewItem, "confirmation");
    items.push(
      "<div class='ua-wizard-success-item'>"
      + "<div class='ua-wizard-success-item-label'>" + label + "</div>"
      + "<div class='ua-wizard-success-item-value'>" + previewHtml + "</div>"
      + "</div>"
    );
  }
  if (items.length === 0) return "";
  return (
    "<section class='ua-wizard-success-section'>"
    + "<h4 class='ua-wizard-success-section-title'>" + _escapeHtml(title) + "</h4>"
    + "<div class='ua-wizard-success-grid'>" + items.join("") + "</div>"
    + "</section>"
  );
}

async function _buildSentGroupsHtml(cfg) {
  if (!(cfg.sentGroups instanceof Array) || cfg.sentGroups.length === 0) {
    return "";
  }

  var groups = [];
  for (var i = 0; i < cfg.sentGroups.length; i++) {
    var group = cfg.sentGroups[i] || {};
    if (!(group.items instanceof Array) || group.items.length === 0) continue;

    var cards = [];
    for (var j = 0; j < group.items.length; j++) {
      var item = group.items[j];
      var previewHtml = await _renderPreviewHtml(item, "confirmation");
      cards.push(
        "<div class='ua-wizard-success-item'>"
        + "<div class='ua-wizard-success-item-label'>" + _escapeHtml(item.label || "") + "</div>"
        + "<div class='ua-wizard-success-item-value'>" + previewHtml + "</div>"
        + "</div>"
      );
    }
    if (cards.length === 0) continue;

    groups.push(
      "<section class='ua-wizard-success-section'>"
      + "<h4 class='ua-wizard-success-section-title d-inline-flex align-items-center gap-2'><span class='ua-wizard-step-pill'>" + _escapeHtml(group.step) + "</span>" + _escapeHtml(group.title || ("Step " + group.step)) + "</h4>"
      + "<div class='ua-wizard-success-grid'>" + cards.join("") + "</div>"
      + "</section>"
    );
  }

  if (groups.length === 0) return "";
  return "<section class='ua-wizard-success-section'><h4 class='ua-wizard-success-section-title'>What you sent:</h4>" + groups.join("") + "</section>";
}

async function _renderSuccessConfirmation(wizard, doc, payload) {
  var cfg = _buildSuccessConfirmationConfig(wizard, doc || {}, payload || {});
  var existing = wizard.querySelector(".ua-wizard-result");
  if (existing) existing.remove();

  var body = wizard.querySelector(".wizard");
  if (!body) return cfg;
  body.classList.add("ua-wizard-complete");

  var sentSection = "";
  if (cfg.showSent) {
    sentSection = await _buildSentGroupsHtml(cfg);
    if (!sentSection) {
      sentSection = await _buildSummarySectionHtml("What you sent:", cfg.sentRows);
    }
  }

  var responseSection = "";
  if (cfg.showAppResponse) {
    responseSection = await _buildSummarySectionHtml("App Response", cfg.responseRows);
  }

  var actionItems = [];
  for (var actionIndex = 0; actionIndex < cfg.actions.length; actionIndex++) {
    var action = cfg.actions[actionIndex] || {};
    var href = _safeText(action.href);
    var label = _safeText(action.label) || "Continue";
    if (!href) continue;
    actionItems.push(
      "<a class='btn btn-primary ua-wizard-success-action' href='" + _escapeHtml(href) + "'>" + _escapeHtml(label) + "</a>"
    );
  }

  var actionsSection = actionItems.length > 0
    ? "<div class='ua-wizard-success-actions d-flex flex-wrap gap-2'>" + actionItems.join("") + "</div>"
    : "";

  body.innerHTML =
    "<div class='ua-wizard-success-wrap'>"
    + "<div class='ua-wizard-success-hero'>"
    + "<div class='ua-wizard-success-icon'><i class='bi bi-" + _escapeHtml(cfg.icon) + "'></i></div>"
    + "<h3 class='ua-wizard-success-title'>" + _escapeHtml(cfg.title) + "</h3>"
    + "<p class='ua-wizard-success-description'>" + _escapeHtml(cfg.description) + "</p>"
    + "</div>"
    + sentSection
    + responseSection
    + actionsSection
    + "</div>";

  return cfg;
}

async function _showStepErrors(wizard, stepNum, doc) {
  var stepEl = wizard.querySelector('[data-wizard-step="' + stepNum + '"]');
  if (!stepEl) return;
  var existing = stepEl.querySelector(".ua-wizard-step-errors");
  if (existing) existing.remove();

  if (!doc || doc.success) return;

  var el = document.createElement("div");
  el.className = "alert alert-danger ua-wizard-step-errors";

  try {
    if (window.UATools && typeof window.UATools.render === "function") {
      var rendered = await window.UATools.render({
        n: "ua.element.validation.error",
        title: "We found a few things to fix.",
        doc: doc
      });
      if (rendered && rendered.nodeType) {
        el.appendChild(rendered);
        stepEl.insertBefore(el, stepEl.firstChild);
        return;
      }
    }
  } catch (error) {
    console.error("Wizard validation render error", error);
  }

  var items = [];
  for (var key in doc) {
    if (key === "success" || !doc[key] || doc[key].success) continue;
    var errs = doc[key].errors;
    if (!(errs instanceof Array)) errs = errs ? [errs] : [];
    for (var i = 0; i < errs.length; i++) {
      var e = errs[i] || {};
      items.push("<li>" + _escapeHtml(e.message || e.description || "Required field missing.") + "</li>");
    }
  }
  el.innerHTML = "<ul class='mb-0 ps-3'>" + items.join("") + "</ul>";
  stepEl.insertBefore(el, stepEl.firstChild);
}

function _clearStepErrors(wizard, stepNum) {
  var stepEl = wizard.querySelector('[data-wizard-step="' + stepNum + '"]');
  if (stepEl) {
    var existing = stepEl.querySelector(".ua-wizard-step-errors");
    if (existing) existing.remove();
  }
}

function _shakeStep(wizard, stepNum) {
  var stepEl = wizard.querySelector('[data-wizard-step="' + stepNum + '"]');
  if (!stepEl) return;
  stepEl.classList.remove("ua-wizard-shake");
  void stepEl.offsetWidth; // force reflow to restart animation
  stepEl.classList.add("ua-wizard-shake");
  setTimeout(function() {
    stepEl.classList.remove("ua-wizard-shake");
  }, 600);
}

// ─── URL prefill ──────────────────────────────────────────────────────────────

function _prefillFromUrl(wizardId) {
  try {
    var params = new URLSearchParams(window.location.search);
    var data = {};
    params.forEach(function(value, key) {
      data[key] = value;
    });
    if (Object.keys(data).length > 0 && window.UATools && typeof window.UATools.load === "function") {
      window.UATools.load(data, "#" + wizardId);
    }
  } catch (e) { /* ignore */ }
}

// ─── Render ───────────────────────────────────────────────────────────────────

/**
 * True only when `value` is an explicit, caller-supplied false.
 *
 * Booleans declared in `define.fields` can reach render() either as a real boolean
 * (JSON authoring) or as the string "false" (attribute authoring), so both count.
 * Anything else — true, "true", undefined, or a framework-injected default — is NOT
 * an explicit false and must not suppress the feature.
 *
 * @param {*} value The raw option value as it arrived in `options`.
 * @returns {boolean}
 */
function _isExplicitFalse(value) {
  return value === false || value === "false";
}

exports.render = function(options) {
  var wizardId = uai.generateRandomID(12);

  if (!(Array.isArray(options.inner))) {
    options.inner = [options.inner];
  }
  // `showSummaryStep` / `summary` / `showSummary` are three aliases for one flag, and
  // each is declared with `default: true`. The framework materialises a declared
  // default for every field the caller OMITTED (uat/src/interface/element.js:370-371),
  // so a caller passing only `showSummaryStep: false` still arrives here with
  // `summary === true` and `showSummary === true` injected. A plain
  // "any alias is true → enable" pass therefore re-enabled the step and the explicit
  // `false` was silently ignored.
  //
  // A defaulted `true` is indistinguishable from an omitted alias, so `true` cannot be
  // trusted as intent while `false` always is: an explicit false on ANY alias wins.
  // Omitting all three still yields the default (enabled), so tenants that rely on the
  // summary step appearing by default are unaffected.
  var summaryEnabled = true;
  if (_isExplicitFalse(options.showSummaryStep) ||
      _isExplicitFalse(options.summary) ||
      _isExplicitFalse(options.showSummary)) {
    summaryEnabled = false;
  }

  if (summaryEnabled === true && options.inner.length > 1) {
    options.inner.push({
      n: "div",
      "data-wizard-title": "Summary",
      i: [
        {
          n: "p",
          c: "ua-wizard-summary-intro text-muted mb-3",
          i: "Review your submission before final save."
        },
        {
          n: "div",
          c: "ua-wizard-summary-list",
          "data-wizard-summary-host": "true",
          i: ""
        }
      ]
    });
  }

  var steps = options.inner.map(function(step, index) {
    var stepContent = step;
    if (step && typeof step === "object" && !(step instanceof Array) && step.wizardHeader && typeof step.wizardHeader === "object") {
      var fallbackTitle = (step["data-wizard-title"] || ("Step " + (index + 1))).toString();
      var headerDoc = _buildStepHeaderDoc(step.wizardHeader, fallbackTitle);
      var stepWithoutHeader = { ...step };
      delete stepWithoutHeader.wizardHeader;

      if (headerDoc) {
        stepContent = {
          n: "div",
          i: [headerDoc, stepWithoutHeader]
        };
      } else {
        stepContent = stepWithoutHeader;
      }
    }

    var cClass = "wizard-step";
    if (index !== 0) cClass += " d-none";
    return {
      "n": "div",
      "data-wizard-id": wizardId,
      "data-wizard-step": index + 1,
      "c": cClass,
      "i": stepContent
    };
  });
  var stepTitles = options.inner.map(function(step, index) {
    if (step && typeof step === "object" && step["data-wizard-title"]) {
      return (step["data-wizard-title"] || "").toString().trim() || ("Step " + (index + 1));
    }
    return "Step " + (index + 1);
  });

  // Build data-wizard-errors attribute from errors field
  var wizardErrorsAttr = "";
  if (options.errors && options.errors.length > 0) {
    try {
      var errMap = {};
      for (var ei = 0; ei < options.errors.length; ei++) {
        var e = options.errors[ei];
        if (e && e.namespace) errMap[e.namespace] = e;
      }
      wizardErrorsAttr = JSON.stringify(errMap);
    } catch (e2) { /* ignore */ }
  }

  // Default continue button content
  var isLastStep = options.inner.length === 1;
  var continueInner = isLastStep
    ? _buildSubmitButtonInner(options.submitText || "")
    : _buildContinueButtonInner(options.continueText || "");

  var navMode = "auto";
  if (options.allowStepJump === true) navMode = "always";
  if (options.allowStepJump === false) navMode = "never";

  var navFree = options.allowStepJump === true;
  if (!navFree && navMode === "auto" && options.allowStepJumpOnEdit !== false && _isEditOptions(options)) {
    navFree = true;
  }
  var showProgressBar = options.showProgressBar === true;

  var initialHasSeen = "[1]";
  if (navFree) {
    var allSeen = [];
    for (var seenStep = 1; seenStep <= options.inner.length; seenStep++) {
      allSeen.push(seenStep);
    }
    initialHasSeen = JSON.stringify(allSeen);
  }

  var wiz = {
    "n": "div",
    "id": wizardId,
    "data-wizard": "true",
    "name": options.name,
    "data-wizard-steps": options.inner.length,
    "data-wizard-current": "1",
    "data-wizard-hasSeen": initialHasSeen,
    "data-wizard-phi": options.phi ? "true" : "",
    "data-wizard-draft-url": options["draft-url"] || "",
    "data-wizard-errors": wizardErrorsAttr,
    "data-wizard-success-title": options.successTitle || "",
    "data-wizard-success-description": options.successDescription || "",
    "data-wizard-success-icon": options.successIcon || "check2-circle",
    "data-wizard-success-document-fields": options.successDocumentFields || "",
    "data-wizard-success-response-fields": options.successResponseFields || "",
    "data-wizard-success-hide-toolbar": options.successHideToolbar === false ? "false" : "true",
    "data-wizard-success-show-document": options.successShowDocument === false ? "false" : "true",
    "data-wizard-success-show-response": options.successShowResponse === false ? "false" : "true",
    "data-wizard-success-show-sent": options.successShowSent === false ? "false" : "true",
    "data-wizard-success-show-app-response": options.successShowAppResponse === false ? "false" : "true",
    "data-wizard-submitting": "false",
    "data-wizard-complete": "false",
    "data-wizard-show-nav": options.showStepNavigation === true ? "true" : "false",
    "data-wizard-nav-mode": navMode,
    "data-wizard-nav-on-edit": options.allowStepJumpOnEdit === false ? "false" : "true",
    "data-wizard-nav-free": navFree ? "true" : "false",
    "data-wizard-editing": _isEditOptions(options) ? "true" : "false",
    "data-wizard-dynamic-url": options.dynamicStepsUrl || "",
    "data-wizard-dynamic-method": options.dynamicStepsMethod || "POST",
    "data-wizard-dynamic-path": options.dynamicStepsDataPath || "auto",
    "data-wizard-dynamic-loaded": "[]",
    "data-wizard-summary-step": summaryEnabled === true ? "true" : "false",
    "data-wizard-disable-default": options.disableDefaultBehavior === true ? "true" : "false",
    "data-wizard-continue-text": options.continueText || "",
    "data-wizard-submit-text": options.submitText || ""
  };

  var topNav = null;
  if (options.showStepNavigation === true && options.inner.length > 1) {
    topNav = {
      "n": "div",
      "c": "wizard-step-nav p-2",
      "data-wizard-step-nav": "true",
      "i": stepTitles.map(function(title, index) {
        var stepNum = index + 1;
        var classes = "btn btn-sm btn-outline-secondary me-2 mb-2";
        if (stepNum === 1) classes += " active";
        return {
          n: "button",
          type: "button",
          c: classes,
          "data-wizard-nav-step": String(stepNum),
          "data-wizard-nav-title": title,
          "data-wizard-id": wizardId,
          title: title,
          i: _buildStepLabel(stepNum, title)
        };
      })
    };
  }

  var wizardChildren = [{
    "n": "div",
    "c": "wizard",
    "i": topNav ? [topNav].concat(steps) : steps
  }];

  if (options.disableDefaultBehavior !== true) {
    var footerChildren = [];
    if (showProgressBar) {
      footerChildren.push({
        "n": "div",
        "c": "d-flex flex-fill p-3",
        "i": {
          "n": "div",
          "data-wizard-id": wizardId,
          "data-wizard-progress": "true",
          "data-wizard-progress-width": ((1 / options.inner.length) * 100) + "%",
          "c": "rounded " + options.progressbarclass + " ua-wizard-progress"
        }
      });
    }
    footerChildren.push({
      "n": "div",
      "c": "",
      "i": {
        "n": "button",
        "type": "button",
        "id": wizardId + "-back",
        "c": "m-2 button btn btn-outline-secondary fw-bold box-shadow d-none",
        "i": [{
          "n": "i",
          "c": "bi bi-arrow-left"
        }, {
          "n": "span",
          "c": "d-none d-lg-inline",
          "i": " Back"
        }]
      }
    });
    footerChildren.push({
      "n": "div",
      "c": "",
      "i": {
        "n": "button",
        "type": "button",
        "id": wizardId + "-continue",
        "c": "m-2 button btn btn-primary fw-bold box-shadow",
        "i": continueInner
      }
    });
    wizardChildren.push({
      "n": "div",
      "c": "wizard-footer",
      "i": footerChildren
    });
  }

  wiz.i = wizardChildren;

  return wiz;

}; module.exports.render = exports.render;

// ─── After render: wire buttons, prefill, load draft ─────────────────────────

events.on("interface.afterrender", async function() {
  // Set initial progress bar widths
  var bars = document.querySelectorAll("[data-wizard-progress='true'][data-wizard-progress-width]");
  for (var i = 0; i < bars.length; i++) {
    var width = bars[i].getAttribute("data-wizard-progress-width");
    if (width) bars[i].style.width = width;
  }

  // Wire each unwired wizard
  var wizards = document.querySelectorAll("[data-wizard='true']");
  for (var w = 0; w < wizards.length; w++) {
    (function(wizard) {
      if (wizard.getAttribute("data-wizard-wired") === "true") return;
      wizard.setAttribute("data-wizard-wired", "true");
      var disableDefaultBehavior = wizard.getAttribute("data-wizard-disable-default") === "true";
      if (disableDefaultBehavior) return;

      var wizardId = wizard.getAttribute("id");
      var totalSteps = Number(wizard.getAttribute("data-wizard-steps"));
      var continueBtn = document.getElementById(wizardId + "-continue");
      var backBtn = document.getElementById(wizardId + "-back");
      var inDashboard = _isDashboardContext(wizard);

      // Attach draft listeners (non-PHI, non-dashboard pages only)
      if (!inDashboard && !_isPhiWizard(wizard)) {
        _attachDraftListeners(wizard);
      }

      // Prefill from URL params (non-dashboard pages only)
      if (!inDashboard) {
        _prefillFromUrl(wizardId);
      }

      // Load draft (non-dashboard pages only)
      if (!inDashboard) {
        if (_isPhiWizard(wizard)) {
          _purgePhiLocalDraft(wizard);
          _loadServerDraft(wizard).then(function(draftData) {
            if (draftData && window.UATools && typeof window.UATools.load === "function") {
              window.UATools.load(draftData, "#" + wizardId);
            }
          });
        } else {
          var draftData = _loadDraft(wizard);
          if (draftData && window.UATools && typeof window.UATools.load === "function") {
            window.UATools.load(draftData, "#" + wizardId);
          }
        }
      }

      // Wire Continue button
      if (continueBtn) {
        continueBtn.addEventListener("click", async function() {
          if (wizard.getAttribute("data-wizard-complete") === "true") return;
          if (wizard.getAttribute("data-wizard-submitting") === "true") return;

          var currentStep = Number(wizard.getAttribute("data-wizard-current"));
          var totalStepsNow = Number(wizard.getAttribute("data-wizard-steps"));

          // 1. Validate current step
          var validation = await _validateStep(wizard, currentStep);
          if (!validation.success) {
            _shakeStep(wizard, currentStep);
            await _showStepErrors(wizard, currentStep, validation);
            return;
          }
          _clearStepErrors(wizard, currentStep);

          // 2. Run per-step handler (can throw to block advancement)
          var stateData = _collectFields(wizard);
          try {
            await RunHandler(wizardId, currentStep, stateData);
          } catch (handlerErr) {
            _shakeStep(wizard, currentStep);
            var resolvedErr = _resolveError(wizard, handlerErr);
            showWizardResult(wizard, "danger", _buildErrorHtml([resolvedErr]), true);
            return;
          }

          // 3. Save draft on step advance (non-dashboard only)
          if (!_isDashboardContext(wizard)) {
            _saveDraft(wizard, stateData);
          }

          // 4. Advance or submit
          try {
            var dynamicAdded = await _loadDynamicStepsIfNeeded(wizard, currentStep);
            if (dynamicAdded > 0) {
              totalStepsNow = Number(wizard.getAttribute("data-wizard-steps") || totalStepsNow);
            }
          } catch (dynamicError) {
            console.error("Wizard dynamic step load error", dynamicError);
          }

          var visibleSteps = _getVisibleWizardSteps(wizard);
          var lastVisibleStep = visibleSteps.length > 0 ? visibleSteps[visibleSteps.length - 1] : totalStepsNow;
          if (currentStep < lastVisibleStep) {
            try {
              var wizardControl = wizard.closest("uae") || wizard.parentElement;
              await load(null, wizardControl, "next");
            } catch (e) {
              console.error("Wizard navigation error", e);
            }
          } else {
            if (_shouldDelegateNestedSubmitToParent(wizard)) {
              var delegated = await _delegateNestedSubmitToParent(wizard);
              if (delegated) return;
            }
            await submitWizard(wizard, continueBtn);
          }
        });
      }

      // Wire Back button
      if (backBtn) {
        backBtn.addEventListener("click", async function() {
          if (wizard.getAttribute("data-wizard-complete") === "true") return;
          if (wizard.getAttribute("data-wizard-submitting") === "true") return;
          try {
            var wizardControl = wizard.closest("uae") || wizard.parentElement;
            await load(null, wizardControl, "back");
          } catch (e) {
            console.error("Wizard back error", e);
          }
        });
      }

      if (wizard.__uaWizardNavHandlerBound !== true) {
        wizard.__uaWizardNavHandlerBound = true;
        wizard.addEventListener("click", async function(event) {
          var navBtn = event.target.closest("[data-wizard-nav-step]");
          var summaryBtn = event.target.closest("[data-wizard-summary-goto]");
          var targetAttr = "";

          if (navBtn && wizard.contains(navBtn)) {
            targetAttr = navBtn.getAttribute("data-wizard-nav-step") || "";
          } else if (summaryBtn && wizard.contains(summaryBtn)) {
            targetAttr = summaryBtn.getAttribute("data-wizard-summary-goto") || "";
          } else {
            return;
          }

          if (wizard.getAttribute("data-wizard-complete") === "true") return;
          if (wizard.getAttribute("data-wizard-submitting") === "true") return;

          var targetStep = Number(targetAttr || "0");
          if (!targetStep) return;

          var hasSeen = [];
          var allowStepJump = wizard.getAttribute("data-wizard-nav-free") === "true";
          try {
            hasSeen = JSON.parse(wizard.getAttribute("data-wizard-hasSeen") || "[1]");
          } catch (error) {
            hasSeen = [1];
          }
          if (!allowStepJump && hasSeen.indexOf(targetStep) < 0) return;

          try {
            var wizardControl = wizard.closest("uae") || wizard.parentElement;
            await load(null, wizardControl, targetStep);
          } catch (error) {
            console.error("Wizard step navigation error", error);
          }
        });
      }

      if (wizard.__uaWizardVisibilitySyncBound !== true) {
        wizard.__uaWizardVisibilitySyncBound = true;
        wizard.addEventListener("ua:wizard:visibility-sync", async function() {
          _syncWizardVisibleStepState(wizard);
          await _renderPreSubmitSummary(wizard);
        });
      }

      _syncWizardVisibleStepState(wizard);
    })(wizards[w]);
  }
});

// ─── Submit ───────────────────────────────────────────────────────────────────

function _extractErrorsFromResponse(wizard, result) {
  var collected = [];

  if (!result) return collected;
  if (result.error) collected.push(result.error);
  if (result.errors instanceof Array) {
    collected = collected.concat(result.errors);
  }
  if (collected.length === 0 && result.success === false) {
    collected.push(result);
  }

  var normalized = [];
  for (var i = 0; i < collected.length; i++) {
    normalized.push(_resolveError(wizard, collected[i]));
  }
  return normalized;
}

function _buildErrorHtml(errors) {
  var list = errors instanceof Array ? errors : [];
  if (list.length === 0) {
    return "<p class='mb-0'>We could not save this step yet. Please review the form and try again.</p>";
  }

  var items = [];
  for (var i = 0; i < list.length; i++) {
    var err = list[i] || {};
    var title = _escapeHtml(err.title || "Error");
    var desc = _escapeHtml(err.description || err.message || "Please review the information and try again.");
    var ns = err.namespace ? ("<div class='small text-muted mt-1'>Namespace: " + _escapeHtml(err.namespace) + "</div>") : "";
    var details = "";
    if (err.errors instanceof Array && err.errors.length > 0) {
      var inner = [];
      for (var j = 0; j < err.errors.length; j++) {
        var issue = err.errors[j] || {};
        var field = issue.field || issue.name || issue.key || issue.path || issue.selector || "";
        var issueText = issue.description || issue.message || issue.title || "One or more fields need attention.";
        var prefix = field ? ("<strong>" + _escapeHtml(field) + ":</strong> ") : "";
        inner.push("<li>" + prefix + _escapeHtml(issueText) + "</li>");
      }
      details = "<ul class='mt-2 mb-0 ps-3'>" + inner.join("") + "</ul>";
    }
    var stack = "";
    if (!err.namespace && err.stacktrace) {
      stack = "<details class='mt-2'><summary>Technical details</summary><pre class='mb-0 small'>" + _escapeHtml(err.stacktrace.toString()) + "</pre></details>";
    }
    items.push("<li><strong>" + title + "</strong><div>" + desc + "</div>" + ns + details + stack + "</li>");
  }

  return "<ul class='mb-0 ps-3'>" + items.join("") + "</ul>";
}

async function submitWizard(wizard, continueBtn) {
  if (wizard.getAttribute("data-wizard-complete") === "true") return;
  if (wizard.getAttribute("data-wizard-submitting") === "true") return;

  var uaeEl = wizard.closest("uae");
  var url = uaeEl ? uaeEl.getAttribute("name") : null;
  if (!url) {
    console.error("Wizard has no name/url attribute.");
    return;
  }

  var wizardId = wizard.getAttribute("id");
  var currentStep = Number(wizard.getAttribute("data-wizard-current"));
  var totalSteps = Number(wizard.getAttribute("data-wizard-steps"));

  // Collect the full document using the same mechanism as save()
  var doc = await _collectDocument(wizard, true);
  var payload = {
    document: doc.flat,    // flat merged fields — server extracts this for processing
    steps: doc.steps,      // per-step breakdown — developer has full context
    wizard: {
      id: wizardId,
      current: currentStep,
      total: totalSteps
    }
  };

  // Show loading state
  var origHTML = continueBtn.innerHTML;
  wizard.setAttribute("data-wizard-submitting", "true");
  _setWizardNavigationState(wizard, true);
  continueBtn.innerHTML = '<span class="spinner-border spinner-border-sm me-1" role="status"></span>Saving\u2026';

  // Remove any previous result message
  var prev = wizard.querySelector(".ua-wizard-result");
  if (prev) prev.remove();

  try {
    var response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });
    var result = {};
    try {
      result = await response.json();
    } catch (jsonError) {
      result = {};
    }

    if (response.ok && result && result.success) {
      var successPayload = _normalizeSuccessfulPayload(result);
      if (successPayload && successPayload.success === false) {
        showWizardResult(
          wizard,
          "danger",
          _buildErrorHtml([
            _resolveError(wizard, {
              title: "Submit Failed",
              description: successPayload.message || "The request was not accepted."
            })
          ]),
          true
        );
        return;
      }
      _clearDraft(wizard);
      var redirect = _resolveSuccessNavigationTarget(successPayload, result || {});
      if (redirect) {
        window.location.href = redirect;
        return;
      }
      var confirmationCfg = await _renderSuccessConfirmation(wizard, doc || {}, successPayload);
      _setWizardCompleteState(wizard, true, confirmationCfg.hideToolbar);
    } else {
      var resolvedErrors = _extractErrorsFromResponse(wizard, result || {});
      showWizardResult(wizard, "danger", _buildErrorHtml(resolvedErrors), true);
    }
  } catch (e) {
    console.error("Wizard submit error", e);
    showWizardResult(wizard, "danger", _buildErrorHtml([_resolveError(wizard, e)]), true);
  } finally {
    var completed = wizard.getAttribute("data-wizard-complete") === "true";
    wizard.setAttribute("data-wizard-submitting", "false");
    if (!completed) {
      _setWizardNavigationState(wizard, false);
      continueBtn.innerHTML = origHTML;
    }
  }
}

// ─── Show inline result alert ─────────────────────────────────────────────────

function showWizardResult(wizard, type, message, isHtml) {
  var existing = wizard.querySelector(".ua-wizard-result");
  if (existing) existing.remove();
  var el = document.createElement("div");
  el.className = "alert alert-" + type + " mt-2 ua-wizard-result";
  if (isHtml) {
    el.innerHTML = message;
  } else {
    el.textContent = message;
  }
  var footer = wizard.querySelector(".wizard-footer");
  if (footer) {
    wizard.insertBefore(el, footer);
  } else {
    wizard.appendChild(el);
  }
}

// ─── Save (UAT interface) ─────────────────────────────────────────────────────

/**
 * Returns the full document the wizard is building, plus wizard state.
 * Called by window.UATools.save() when the wizard's uae element is encountered.
 * Always collects data regardless of validation state so drafts can be saved.
 * @param {*} name The name of the control.
 * @param {*} control The control (uae element) to save.
 * @param {*} repo The repo with prevalidation results.
 * @returns repo with data = { document, steps, wizard }
 */
async function save(name, control, repo) {
  var wizard = control.querySelector("[data-wizard='true']");
  var wizardId = wizard.getAttribute("id");
  var currentStep = Number(wizard.getAttribute("data-wizard-current"));
  var totalSteps = Number(wizard.getAttribute("data-wizard-steps"));

  var doc = await _collectDocument(wizard, true);

  repo.data = {
    document: doc.flat,    // flat merged fields across all steps
    steps: doc.steps,      // per-step breakdown
    wizard: {
      id: wizardId,
      current: currentStep,
      total: totalSteps
    }
  };

  return repo;

} module.exports.save = save;

module.exports._test = {
  normalizeSuccessfulPayload: _normalizeSuccessfulPayload,
  resolveSuccessNavigationTarget: _resolveSuccessNavigationTarget,
  buildSuccessConfirmationConfig: _buildSuccessConfirmationConfig,
  submitWizard: submitWizard,
  _collectDocument: _collectDocument,
  _getTopLevelStepControls: _getTopLevelStepControls,
  _getTopLevelStepControlNames: _getTopLevelStepControlNames,
  _saveTopLevelControl: _saveTopLevelControl,
  _isLikelyRouteTarget: _isLikelyRouteTarget,
  _findParentWizard: _findParentWizard,
  _shouldDelegateNestedSubmitToParent: _shouldDelegateNestedSubmitToParent
};

// ─── Load (UAT interface — step navigation) ───────────────────────────────────

/**
 * Navigate the wizard to a different step.
 * @param {*} name The name of the control.
 * @param {*} control The control's parent element.
 * @param {*} data "next", "back", or a step number.
 */
async function load(name, control, data) {

  var wizard = control.querySelector("[data-wizard='true']");
  if (wizard.getAttribute("data-wizard-complete") === "true") return;
  if (wizard.getAttribute("data-wizard-submitting") === "true") return;
  if (wizard.getAttribute("data-wizard-nav-mode") === "auto" && wizard.getAttribute("data-wizard-nav-on-edit") !== "false") {
    _setWizardStepJumpState(wizard, _isLikelyEditingWizard(wizard));
  }
  var wizardId = wizard.getAttribute("id");
  var currentStep = Number(wizard.getAttribute("data-wizard-current"));
  var totalSteps = Number(wizard.getAttribute("data-wizard-steps"));
  var visibleSteps = _getVisibleWizardSteps(wizard);
  if (visibleSteps.length === 0) visibleSteps = [currentStep];
  var currentVisibleIndex = visibleSteps.indexOf(currentStep);
  if (currentVisibleIndex < 0) currentVisibleIndex = 0;
  var nextStep = currentStep;

  switch (data) {
    case "next":
      if (currentVisibleIndex < visibleSteps.length - 1) {
        nextStep = visibleSteps[currentVisibleIndex + 1];
      } else {
        throw new Error("Cannot go to the next step, reached the end of the wizard.");
      }
      break;
    case "back":
      if (currentVisibleIndex > 0) {
        nextStep = visibleSteps[currentVisibleIndex - 1];
      } else {
        throw new Error("Cannot go back, reached the beginning of the wizard.");
      }
      break;
    default:
      data = parseInt(data);
      if (isNaN(data)) {
        throw new Error("Invalid data type, must be 'next', 'back', or a number.");
      }
      if (visibleSteps.indexOf(data) >= 0) {
        nextStep = data;
      } else {
        throw new Error("Invalid step number, must target a visible wizard step.");
      }
      break;
  }

  // Hide current step
  var currentStepDiv = control.querySelector('[data-wizard-id="' + wizardId + '"][data-wizard-step="' + currentStep + '"]');
  if (currentStepDiv) currentStepDiv.classList.add("d-none");

  // Show next step
  var nextStepDiv = control.querySelector('[data-wizard-id="' + wizardId + '"][data-wizard-step="' + nextStep + '"]');
  if (nextStepDiv) nextStepDiv.classList.remove("d-none");

  // Update progress bar
  var progress = control.querySelector('[data-wizard-id="' + wizardId + '"][data-wizard-progress="true"]');
  if (progress) {
    var nextVisibleIndex = visibleSteps.indexOf(nextStep);
    var denominator = visibleSteps.length || totalSteps || 1;
    var numerator = nextVisibleIndex >= 0 ? (nextVisibleIndex + 1) : 1;
    progress.style.width = (numerator === denominator ? 100 : (numerator / denominator) * 100) + "%";
  }

  // Show/hide back button
  var btnBack = document.getElementById(wizardId + "-back");
  if (btnBack) {
    if (visibleSteps.indexOf(nextStep) > 0) {
      btnBack.classList.remove("d-none");
    } else {
      btnBack.classList.add("d-none");
    }
  }

  // Update Continue button label for last vs. intermediate step
  var btnContinue = document.getElementById(wizardId + "-continue");
  if (btnContinue) {
    var continueText = (wizard.getAttribute("data-wizard-continue-text") || "").toString();
    var submitText = (wizard.getAttribute("data-wizard-submit-text") || "").toString();
    var lastVisibleStep = visibleSteps.length > 0 ? visibleSteps[visibleSteps.length - 1] : totalSteps;
    if (nextStep === lastVisibleStep) {
      if (submitText.length > 0) {
        btnContinue.innerHTML = '<span class="d-lg-inline">' + _escapeHtml(submitText) + '&nbsp;</span><i class="bi bi-check2"></i>';
      } else {
        btnContinue.innerHTML = '<i class="bi bi-check2"></i><span class="d-none d-lg-inline"> Submit</span>';
      }
    } else {
      if (continueText.length > 0) {
        btnContinue.innerHTML = '<span class="d-lg-inline">' + _escapeHtml(continueText) + '&nbsp;</span><i class="bi bi-arrow-right"></i>';
      } else {
        btnContinue.innerHTML = '<i class="bi bi-arrow-right"></i><span class="d-none d-lg-inline"> Continue</span>';
      }
    }
  }

  // Track seen steps and reset validation for newly visited steps
  var hasSeen = JSON.parse(wizard.getAttribute("data-wizard-hasSeen") || "[1]");
  if (!hasSeen.includes(nextStep)) {
    ResetValidation('[data-wizard-id="' + wizardId + '"][data-wizard-step="' + nextStep + '"]');
    hasSeen.push(nextStep);
  }
  wizard.setAttribute("data-wizard-hasSeen", JSON.stringify(hasSeen));

  // Update current step
  wizard.setAttribute("data-wizard-current", nextStep);
  _updateStepNavigationState(wizard);
  await _renderPreSubmitSummary(wizard);

  // Scroll the new step into view. Modal-hosted wizards scroll their own
  // internal .modal-body (Bootstrap's modal-dialog-scrollable container), so
  // resetting that is enough and never touches the page. Inline wizards (e.g.
  // /reserve) have no such container — for those, only bring the wizard back
  // into view if the step change has scrolled it out of the viewport, instead
  // of unconditionally yanking the whole page back to the top.
  var scrollHost = wizard.closest(".modal-body");
  if (scrollHost) {
    scrollHost.scrollTop = 0;
  } else {
    var wizardRect = wizard.getBoundingClientRect();
    if (wizardRect.top < 0 || wizardRect.top > window.innerHeight * 0.5) {
      wizard.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

} module.exports.load = load;

// ─── Validation reset ─────────────────────────────────────────────────────────

async function ResetValidation(selector) {
  var el = document.querySelector(selector);
  if (!el) return;
  el.querySelectorAll(".is-invalid").forEach(function(e) { e.classList.remove("is-invalid"); });
  el.querySelectorAll(".is-valid").forEach(function(e) { e.classList.remove("is-valid"); });
}

// ─── Handlers ─────────────────────────────────────────────────────────────────

/**
 * Add a handler to the wizard.
 * Supports wildcards: set wizardID and/or step to null/false for wildcard matching.
 * @param {*} wizardID The wizard ID to attach the handler to.
 * @param {*} onStep The step to attach the handler to.
 * @param {*} myFunction The function to run. Can throw to block step advancement.
 */
function AddHandler(wizardID, onStep, myFunction) {
  handlerz.push({
    wizard: wizardID,
    step: onStep,
    handler: myFunction
  });
} module.exports.AddHandler = AddHandler;

/**
 * Run the handler(s) for the given wizard and step.
 * @param {*} wizardID The id of the wizard.
 * @param {*} step The step number.
 * @param {*} StateOfTheWizard The current field data collected from the wizard.
 */
async function RunHandler(wizardID, step, StateOfTheWizard) {
  for (var i = 0; i < handlerz.length; i++) {
    var handler = handlerz[i];

    if (!handler.wizard && !handler.step) {
      // wildcard: runs on any wizard, any step
    } else if (!handler.wizard && handler.step == step) {
      // step wildcard: runs on any wizard at this step
    } else if (handler.wizard == wizardID && handler.step == step) {
      // specific wizard and step
    } else {
      continue;
    }

    await handler.handler(StateOfTheWizard);
  }
}

// ─── Public API ───────────────────────────────────────────────────────────────

try {
  window.wizard = {
    AddHandler: AddHandler,
    /**
     * Register multiple error overrides into the global registry.
     * @param {Array} arr Array of { namespace, title, description } objects.
     * @param {number} priority 1=dashboard, 2=plugin, 3=app. Higher wins.
     */
    registerErrors: function(arr, priority) {
      if (!(arr instanceof Array)) return;
      for (var i = 0; i < arr.length; i++) {
        var item = arr[i];
        if (!item) continue;
        var ns = item.namespace || item.id || "";
        _registerError(ns, item, typeof priority === "number" ? priority : ERROR_PRIORITY.dashboard);
      }
    },
    /**
     * Register a single error override.
     * @param {string} namespace The error namespace.
     * @param {object} def { title, description, actions, close }.
     * @param {number} priority 1=dashboard, 2=plugin, 3=app.
     */
    registerError: function(namespace, def, priority) {
      _registerError(namespace, def, typeof priority === "number" ? priority : ERROR_PRIORITY.dashboard);
    }
  };
} catch (error) {
  console.error("No window to attach the wizard handler to.");
}
